import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Pause, Play, Save, Activity } from "lucide-react";
import { Stat, Section, Loading, Empty, useFetch, dt } from "./_shared";

export default function Ops() {
  const { data: sched, reload: reloadSched } = useFetch("/admin/scheduler/status");
  const { data: backups, reload: reloadBackups } = useFetch("/admin/pre-launch/backups");
  const { data: reviewers } = useFetch("/admin/pre-launch/reviewer-status");
  const [busy, setBusy] = useState(false);
  const [health, setHealth] = useState(null);

  const pause = async () => { setBusy(true); try { await api.post("/admin/scheduler/pause"); toast.success("Scheduler pausiert."); await reloadSched(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };
  const resume = async () => { setBusy(true); try { await api.post("/admin/scheduler/resume"); toast.success("Scheduler fortgesetzt."); await reloadSched(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };
  const backup = async () => { setBusy(true); try { await api.post("/admin/pre-launch/backup?label=manual"); toast.success("Backup erstellt."); await reloadBackups(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };
  const runHealth = async () => { setBusy(true); try { const { data } = await api.get("/admin/pre-launch/db-health-check"); setHealth(data); toast.success("DB-Check fertig."); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };

  const files = backups?.files || []; const accounts = reviewers?.accounts || [];
  const healthSteps = health ? (health.steps || health.checks || []) : [];

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin" title="Betrieb" subtitle="Scheduler, Backups, DB-Health & Reviewer." />

      <Section title="Scheduler" testId="admin-ops-scheduler"
        action={<div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy} onClick={pause} data-testid="admin-ops-pause"><Pause className="h-4 w-4" /> Pause</Button>
          <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy} onClick={resume} data-testid="admin-ops-resume"><Play className="h-4 w-4" /> Start</Button>
        </div>}>
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Läuft" value={sched?.running ? "Ja" : "Nein"} tone={sched?.running ? "good" : "warn"} />
          <Stat label="Jobs" value={sched?.job_count ?? "—"} />
          <Stat label="Verfügbar" value={sched?.available ? "Ja" : "Nein"} />
        </div>
      </Section>

      <Section title="Backups" testId="admin-ops-backups"
        action={<Button size="sm" className="gap-1.5 rounded-full" disabled={busy} onClick={backup} data-testid="admin-ops-backup"><Save className="h-4 w-4" /> Backup erstellen</Button>}>
        {files.length === 0 ? <Empty text="Keine Backups." /> : (
          <div className="space-y-1.5">
            {files.slice(0, 15).map((f, i) => (
              <div key={f.filename || i} className="flex items-center justify-between gap-2 text-sm"><span className="truncate text-foreground">{f.filename || f.name || String(f)}</span><span className="shrink-0 text-xs text-muted-foreground">{f.size_human || f.human || ""}{f.created_at ? ` · ${dt(f.created_at)}` : ""}</span></div>
            ))}
          </div>
        )}
      </Section>

      <Section title="DB-Health-Check" testId="admin-ops-dbhealth"
        action={<Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy} onClick={runHealth} data-testid="admin-ops-dbcheck"><Activity className="h-4 w-4" /> Prüfen</Button>}>
        {!health ? <p className="text-sm text-muted-foreground">Noch nicht geprüft.</p> : healthSteps.length === 0 ? <p className="text-sm text-emerald-600">OK</p> : (
          <div className="space-y-1.5">
            {healthSteps.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-sm"><span className="text-foreground">{s.step || s.name}</span><span className={s.ok ? "text-emerald-600" : "text-rose-600"}>{s.ok ? "OK" : "Fehler"}{s.ms != null ? ` · ${Math.round(s.ms)}ms` : ""}</span></div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Reviewer-Konten" testId="admin-ops-reviewers">
        {accounts.length === 0 ? <Empty text="—" /> : (
          <div className="space-y-1.5">
            {accounts.map((a, i) => (
              <div key={a.email || i} className="flex items-center justify-between gap-2 text-sm"><span className="truncate text-foreground">{a.email}</span><span className="shrink-0 text-xs text-muted-foreground">{a.exists ? `${a.tier || "—"}${a.active ? " · aktiv" : ""}` : "fehlt"}</span></div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
